import { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { EntryIntercomSpec } from "../scenario/types";

const DEG = Math.PI / 180;

/** Screen-space pin (percent of viewport) for the intercom screen centre. */
export type IntercomPin = { xPct: number; yPct: number; visible: boolean; distance: number };

/** Live position/rotation override for wall calibration: `?intercomPos=x,y,z`
 *  and `?intercomRot=deg`. Matches the project's `?pinYaw=` / `?beat=` pattern
 *  so the wall placement can be nudged in a real browser without a redeploy. */
function readOverride(spec: EntryIntercomSpec): {
  position: [number, number, number];
  yaw: number;
} {
  let position = spec.position;
  let rot = spec.rotationDeg ?? 0;
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search);
    const p = q.get("intercomPos");
    if (p) {
      const parts = p.split(",").map(Number);
      if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
        position = [parts[0], parts[1], parts[2]];
      }
    }
    const r = q.get("intercomRot");
    if (r != null && r !== "" && Number.isFinite(Number(r))) rot = Number(r);
  }
  return { position, yaw: rot * DEG };
}

/** Idle "video door station" screen graphic (CSP-safe canvas). */
function makeIdleTexture(label: string): THREE.CanvasTexture {
  const w = 384;
  const h = 512;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  const grad = g.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "#0f1b1b");
  grad.addColorStop(1, "#081010");
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  // faint scanlines so it reads as a live camera feed
  g.fillStyle = "rgba(120,220,205,0.05)";
  for (let y = 0; y < h; y += 6) g.fillRect(0, y, w, 2);
  // top status bar
  g.fillStyle = "rgba(120,220,205,0.9)";
  g.font = "600 22px Segoe UI, Arial, sans-serif";
  g.textAlign = "left";
  g.fillText("● " + label, 22, 40);
  g.textAlign = "right";
  g.fillStyle = "rgba(255,120,120,0.95)";
  g.fillText("LIVE", w - 22, 40);
  // camera reticle
  g.strokeStyle = "rgba(150,230,215,0.5)";
  g.lineWidth = 3;
  g.strokeRect(w / 2 - 60, h / 2 - 70, 120, 140);
  g.beginPath();
  g.arc(w / 2, h / 2, 30, 0, Math.PI * 2);
  g.stroke();
  // caption
  g.fillStyle = "rgba(210,245,238,0.85)";
  g.textAlign = "center";
  g.font = "500 22px Segoe UI, Arial, sans-serif";
  g.fillText("Press to answer", w / 2, h - 54);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/**
 * A wall-mounted video entry intercom / door station: a slim dark backplate
 * flush against a wall, a portrait screen (live-camera idle graphic), a speaker
 * grille, and a glowing call button. Sits at `spec.position` (a point ON the
 * wall) facing `spec.rotationDeg`. Projects the screen centre to viewport % each
 * frame via `onPin` so App can align an overlay / proximity hotspot.
 */
export function EntryIntercom({
  spec,
  onPin,
}: {
  spec: EntryIntercomSpec;
  onPin: (pin: IntercomPin) => void;
}) {
  const { camera } = useThree();
  const screenAnchor = useRef<THREE.Group>(null);
  const worldPos = useRef(new THREE.Vector3()).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toPin = useRef(new THREE.Vector3()).current;
  const last = useRef<IntercomPin>({ xPct: 0, yPct: 0, visible: false, distance: 999 });
  const onPinRef = useRef(onPin);
  onPinRef.current = onPin;

  const { position, yaw } = useMemo(() => readOverride(spec), [spec]);
  const scale = spec.scale ?? 1;
  const idleTex = useMemo(() => makeIdleTexture(spec.label ?? "ENTRY"), [spec.label]);

  useFrame(() => {
    const anchor = screenAnchor.current;
    if (!anchor) return;
    anchor.getWorldPosition(worldPos);
    camera.getWorldDirection(camDir);
    toPin.copy(worldPos).sub(camera.position);
    const distance = toPin.length();
    const inFront = toPin.normalize().dot(camDir) > 0.1;
    const proj = worldPos.clone().project(camera);
    const xPct = (proj.x * 0.5 + 0.5) * 100;
    const yPct = (-proj.y * 0.5 + 0.5) * 100;
    const onScreen = xPct > -5 && xPct < 105 && yPct > -5 && yPct < 105;
    const pin: IntercomPin = { xPct, yPct, visible: inFront && onScreen && distance < 6, distance };
    const p = last.current;
    if (
      p.visible !== pin.visible ||
      Math.abs(p.xPct - pin.xPct) > 0.3 ||
      Math.abs(p.yPct - pin.yPct) > 0.3 ||
      Math.abs(p.distance - pin.distance) > 0.05
    ) {
      last.current = pin;
      onPinRef.current(pin);
    }
    if (typeof window !== "undefined") {
      (window as unknown as { __intercomPin?: IntercomPin }).__intercomPin = pin;
    }
  });

  // Panel builds forward from the wall along +Z (before yaw). z=0 is the wall
  // face; everything protrudes slightly into the room (+Z).
  return (
    <group position={position} rotation={[0, yaw, 0]} scale={scale}>
      {/* Backplate flush on the wall */}
      <mesh position={[0, 0, 0.012]}>
        <boxGeometry args={[0.3, 0.46, 0.03]} />
        <meshStandardMaterial color="#161a20" metalness={0.5} roughness={0.45} />
      </mesh>
      {/* Screen (portrait, emissive so it reads as "on") */}
      <group position={[0, 0.06, 0.03]}>
        <group ref={screenAnchor} position={[0, 0, 0.001]}>
          <mesh>
            <planeGeometry args={[0.24, 0.3]} />
            <meshStandardMaterial
              map={idleTex}
              emissive="#ffffff"
              emissiveMap={idleTex}
              emissiveIntensity={0.7}
              toneMapped={false}
            />
          </mesh>
        </group>
      </group>
      {/* Speaker grille (rows of holes suggested with a dark ridged strip) */}
      <mesh position={[0, -0.14, 0.03]}>
        <boxGeometry args={[0.2, 0.05, 0.006]} />
        <meshStandardMaterial color="#0c0f13" metalness={0.3} roughness={0.8} />
      </mesh>
      {/* Glowing call button (cylinder laid flat so its round face points +Z) */}
      <mesh position={[0, -0.2, 0.032]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.018, 0.018, 0.012, 24]} />
        <meshStandardMaterial color="#4aa89c" emissive="#4aa89c" emissiveIntensity={1.5} toneMapped={false} />
      </mesh>
    </group>
  );
}
